/*
 * Sign-in and consent dialogs shown when an existing request needs authentication recovery.
 * AccountLayers mounts the presenter; the recovery service supplies each pending attempt.
 * The request remains with the service while the dialog collects credentials or navigation.
 * Example: registerComponents(registry);
 *          <AuthRecoveryLayer context={context} controller={recovery}/>
 */

import { UIElement, defineUIElements } from '../../runtime/components/ui-element.js';
import { servicesContext } from '../../runtime/services.js';
import { createAuthRecovery, recoveryCancelled } from './auth-recovery.js';
import { registerComponents as registerSignIn, SignIn } from './sign-in-form.jsx';
import {
  registerComponents as registerAuthored,
  AuthoredContent
} from '../editing/authored-content.jsx';

export class AuthRecoverySession extends UIElement {
  static properties = {
    request: { attribute: false },
    consent: { state: true },
    error: { state: true }
  };

  constructor() {
    super();
    this.consent = null;
    this.error = '';
    this.captureCredentials = (node) => (this.credentials = node);
    this.captureOverlay = (node) => (this.overlay = node);
    this.captureCard = (node) => (this.card = node);
    this.captureAgree = (node) => (this.agreeNode = node);
    this.bindMethods('authenticate', 'navigate');
    this.own(() => {
      this.rejectConsent?.(recoveryCancelled());
      this.resolveConsent = this.rejectConsent = null;
    });
  }

  updated(changed) {
    super.updated(changed);
    const life = this.connection;
    if (!life || !this.card || !this.credentials || !this.request) return;
    servicesContext(this.context, ['a11y']);
    if (this.boundConnection !== life) {
      this.boundConnection = life;
      this.renderTask(
        life.ensure('modal', async () => {
          await this.credentials.whenReady();
          if (life.signal.aborted || this.request.signal.aborted) return;
          const request = this.request;
          const modal = this.context.services.a11y.openModal({
            overlay: this.overlay,
            card: this.card,
            initialFocus: request.attempt.user
              ? this.credentials.passwordInput
              : this.credentials.usernameInput,
            closeOnEscape: !request.attempt.required,
            closeOnOverlayClick: !request.attempt.required,
            inertBackground: true,
            signal: AbortSignal.any([life.signal, request.signal]),
            onClose: () => request.finish()
          });
          this.modal = modal;
          return modal;
        })
      );
    }
    if (this.focusAgreement && this.agreeNode) {
      this.focusAgreement = false;
      this.agreeNode.focus({ preventScroll: true });
    }
  }

  stopClick(event) {
    event.stopPropagation();
  }
  leave() {
    return this.request.leave();
  }
  navigate(path) {
    return this.request.navigate(path);
  }

  authenticate(data) {
    const request = this.request;
    return request.authenticate(
      data,
      (consent) =>
        new Promise((resolve, reject) => {
          this.resolveConsent = resolve;
          this.rejectConsent = reject;
          this.consent = consent;
          this.error = '';
          this.focusAgreement = true;
        })
    );
  }

  async agree() {
    const consent = this.consent,
      request = this.request;
    if (!consent || request.signal.aborted) return;
    this.error = '';
    try {
      await request.accept(consent);
      if (this.disposed || request.signal.aborted) return;
      this.consent = null;
      const resolve = this.resolveConsent;
      this.resolveConsent = this.rejectConsent = null;
      resolve();
    } catch (error) {
      if (!request.signal.aborted && !this.disposed) this.error = error.message;
    }
  }

  closeNative() {
    this.modal?.close();
  }

  render() {
    const request = this.request;
    if (!request) return null;
    const heading =
      this.consent?.doc.title || (request.publicLogin ? 'Sign in' : 'Sign in to continue');
    return this.portal(
      'recovery',
      'body',
      <div ref={this.captureOverlay} class="modal-overlay auth-recovery-overlay" onClick={this.stopClick}>
        <div ref={this.captureCard} class="modal-card auth-recovery-card" role="dialog" aria-modal="true" aria-labelledby="auth-recovery-title" tabindex="-1">
          <h2 id="auth-recovery-title">{heading}</h2>
          <div>
            <SignIn
              ref={this.captureCredentials}
              username={request.attempt.user?.username || ''}
              expired={!!request.attempt.user}
              ownerSignal={request.signal}
              links={request.publicLogin}
              formHidden={!!this.consent}
              onNavigate={this.navigate}
              onCredentials={this.authenticate}/>
            {this.consent ? (
              <section class="auth-recovery-consent">
                <AuthoredContent
                  class="prose-post coc-body"
                  value={this.consent.doc.body || ''}
                  reportImages={false}/>
                <div class="alert-error" role="alert" hidden={!this.error}>{this.error}</div>
                <div class="modal-actions">
                  <button
                    ref={this.captureAgree}
                    type="button"
                    class="button-primary"
                    command={this.agree}>
                    I agree
                  </button>
                </div>
              </section>
            ) : null}
          </div>
          <div class="modal-actions">
            <button type="button" class="button-secondary" onClick={this.leave}>
              {request.attempt.required ? 'Go to home' : 'Cancel'}
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export class AuthRecoveryLayer extends UIElement {
  static properties = { controller: { attribute: false }, request: { state: true } };

  constructor() {
    super();
    this.request = null;
    this.capture = (node) => (this.session = node);
    this.bindMethods('present');
  }

  updated(changed) {
    super.updated(changed);
    const life = this.connection;
    if (!life) return;
    const controller = this.controller || this.context.service(createAuthRecovery);
    if (this.boundConnection !== life || this.boundController !== controller) {
      this.releasePresenter?.();
      this.boundConnection = life;
      this.boundController = controller;
      const release = controller.attach(this.present);
      this.releasePresenter = release;
      life.own(() => {
        release();
        if (this.releasePresenter === release) this.releasePresenter = null;
      });
    }
  }

  present(request) {
    const session = this.session;
    this.request = request;
    if (!request && session) {
      try {
        session.closeNative();
      } finally {
        session.dispose();
      }
    }
    return request ? this.whenRendered() : undefined;
  }

  render() {
    return (
      <>
        {this.request
          ? [<AuthRecoverySession key={this.request} ref={this.capture} request={this.request}/>]
          : null}
      </>
    );
  }
}

export function registerComponents(registry) {
  registerSignIn(registry);
  registerAuthored(registry);
  defineUIElements(registry, {
    'tbc-auth-recovery-session': AuthRecoverySession,
    'tbc-auth-recovery-layer': AuthRecoveryLayer
  });
}
